import React from "react";
import "./statCard.css";

/**
 * The single stat / KPI tile used everywhere in the app.
 *
 * Props:
 *  - label     : the metric caption (rendered uppercase)
 *  - value     : the metric itself — a node. The CALLER formats it
 *                (currencyFormatter, toFixed, %, …); this card never formats.
 *  - suffix    : small trailing unit next to the value, e.g. "%", "days", "/ 12"
 *  - sub       : supporting caption under the value
 *  - icon      : a React node (an Ant Design icon) shown in a tinted plate
 *  - accent    : accent colour driving the gradient, glow, icon plate and hover
 *                (default OM green). Everything is derived from it via color-mix.
 *  - progress  : optional 0–100 number (or { percent, color }) → thin bar
 *  - loading   : shimmer skeleton instead of content
 *  - onClick   : makes the tile a keyboard-accessible button
 *  - children  : extra slot below the value (tag chips, segmented bars, legends)
 *  - className : extra classes on the root
 *
 * Wrap a row of these in <StatGrid> (or the .om-stat-grid class).
 */
const StatCard = ({
  label,
  value,
  suffix,
  sub,
  icon,
  accent = "#189F6B",
  progress,
  loading = false,
  onClick,
  children,
  className = "",
  ...rest
}) => {
  const pct =
    typeof progress === "number"
      ? progress
      : progress && typeof progress === "object"
        ? progress.percent
        : null;
  const pctColor = progress && typeof progress === "object" ? progress.color : null;

  const clickable = typeof onClick === "function";

  const classes = [
    "om-stat",
    clickable ? "om-stat--clickable" : "",
    loading ? "om-stat--loading" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (loading) {
    return (
      <div className={classes} style={{ "--stat-accent": accent }} {...rest}>
        <div className="om-stat__skeleton om-stat__skeleton--icon" />
        <div className="om-stat__body">
          <div className="om-stat__skeleton om-stat__skeleton--label" />
          <div className="om-stat__skeleton om-stat__skeleton--value" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={classes}
      style={{ "--stat-accent": accent }}
      {...(clickable
        ? {
            role: "button",
            tabIndex: 0,
            onClick,
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            },
          }
        : {})}
      {...rest}
    >
      {icon && <div className="om-stat__icon">{icon}</div>}

      <div className="om-stat__body">
        {label && <div className="om-stat__label">{label}</div>}

        <div className="om-stat__value">
          {value}
          {suffix ? <span className="om-stat__suffix">{suffix}</span> : null}
        </div>

        {sub ? <div className="om-stat__sub">{sub}</div> : null}

        {pct !== null && pct !== undefined ? (
          <div className="om-stat__progress">
            <div
              className="om-stat__progress-fill"
              style={{
                width: `${Math.max(0, Math.min(100, pct))}%`,
                ...(pctColor ? { background: pctColor } : {}),
              }}
            />
          </div>
        ) : null}

        {children ? <div className="om-stat__extra">{children}</div> : null}
      </div>
    </div>
  );
};

/** Responsive grid wrapper for a row of StatCards. */
export const StatGrid = ({ children, className = "", ...rest }) => (
  <div className={`om-stat-grid ${className}`.trim()} {...rest}>
    {children}
  </div>
);

export default StatCard;
